import System from '../System';
import GameObject from '../../GameObject/GameObject';
import Animator from '../../Prototype/Animator';
import Field, { FieldEvent } from '../../Prototype/World/Field';
import { StartAnimationClip } from '../Animation/AnimationHelper';
import Game from '../../Game';
import { WorldDataHelper } from '../../Utility/WorldDataHelper';
import WorldMap from '../../Prototype/World/WorldMap';
import WorldSystem from '../World/WorldSystem';
import Container from '../../Prototype/Container';
import { RenderSystemEvent } from '../Render/RenderSystem';
import FieldComponent from '../../Component/Map/Field';
import { AUDIO_PACKS } from '../Audio';
import { ResourceDriver } from '../../Utility/Resource/ResourceManager';
import { IGameState } from '../../State/Game/IGameState';
import { getCropUnlockShare, getFieldUnlockShare } from '../../Constants/AvatarsAndCrops';
import WorldScene from '../../Scene/WorldScene';
import { HarvestOffWorldCrops } from 'System/Animation/Clip';
import { PlayerActions } from 'State/Player/PlayerActions';

export default class FieldSystem extends System {
    public animator: Animator;
    public fields: Field[];
    public fieldComponents: FieldComponent[];
    public activeFields: Field[];
    public scene: WorldScene;

    public init(): void {}

    public async preload(): Promise<any> {
        // Queue all required card audios from audio sprite sheet.
        // please take a look at README on resources/audio directory.
        Game.resource.add(AUDIO_PACKS.fieldSystem, undefined, ResourceDriver.SECONDARY);

        const state: IGameState = this.store.getState();
        this.addShareables(state);
        Game.resource.add(
            `atlas-map-${state.worldState.worldNumber}`,
            `resource/graphics/sprite_sheets/map-package-${state.worldState.worldNumber}.json`
        );

        for (let worldNumber = 1; worldNumber <= state.playerState.maxWorld; worldNumber++) {
            Game.resource.add(
                `crop_icons_${worldNumber}`,
                `resource/graphics/sprite_sheets/crop-icon-package-${worldNumber}.json`
            );
        }

        await Game.resource.load();
    }
    public onUpdate(): void {
        this.activeFields.forEach((field: Field) => field.update());
    }

    public onStart(): void {
        this.animator = GameObject.Instantiate(Animator);
        this.scene.addGameObject(this.animator);
        this.scene.on('HarvestCrops', this.harvestOffWorldCrops.bind(this));
        this.createFields();
        this.scene.emit(FieldEvent.UNLOCK);
        this.unlockFieldsFromPreviousWorlds(Game.store.getState());
    }

    public onDestroy(): void {}

    public createFields(): void {
        const state: IGameState = Game.store.getState();
        const worldMap: WorldMap = this.scene.getSystem(WorldSystem).worldMap;
        const fieldContainer: Container = GameObject.Instantiate(Container, {
            name: 'field_container',
        });
        this.fields = [];
        this.fieldComponents = [];

        const worldNumber = state.worldState.worldNumber;
        const fields = WorldDataHelper.getAllFieldsData(worldNumber);

        // Create each field in the world and add to container
        fields.forEach((fieldData, fieldIndex) => {
            const field: Field = GameObject.Instantiate(Field, {
                fieldData,
                worldNumber,
                fieldIndex,
                worldLayout: this.scene.layout,
                signBubblesContainer: worldMap.signBubblesContainer,
            });
            this.fields.push(field);
            this.fieldComponents.push(field.getComponent(FieldComponent));
            fieldContainer.addChild(field);
        });

        // Add to map
        // TODO: This should be fixed so that its no longer require to update with each new container added
        worldMap.addChild(fieldContainer);

        [
            worldMap.mapComponentContainer,
            worldMap.friendIconContainer,
            worldMap.luckyWheelContainer,
            worldMap.petContainer,
            worldMap.playerIcon,
            worldMap.signBubblesContainer,
        ].forEach(container => {
            this.scene.emit(RenderSystemEvent.BRING_TO_FRONT, container);
        });

        // Get active fields
        this.activeFields = this.fields.filter((field: Field) => field.isActiveWorld);
    }

    public handleStateChanged(state: IGameState) {}

    protected onLateUpdate(): void {}

    protected onStop(): void {}

    protected harvestOffWorldCrops(): void {
        StartAnimationClip(new HarvestOffWorldCrops(this.animator));
    }

    private addShareables(state: IGameState) {
        WorldDataHelper.getAllFieldsData().forEach(field => {
            // New crop unlock asset
            if (
                Game.player.getHighestLevelCompleted(state) >= field.requiredLevel[0] &&
                state.playerState.fields.indexOf(`${field.id}_0`) === -1
            ) {
                const { key, path } = getCropUnlockShare(field.id);
                Game.resource.add(key, path);
            }
            // New field unlock asset
            if (
                Game.player.getHighestLevelCompleted(state) >= field.requiredLevel[1] &&
                state.playerState.fields.indexOf(`${field.id}_1`) === -1
            ) {
                const { key, path } = getFieldUnlockShare(field.id);
                Game.resource.add(key, path);
            }
        });
    }

    private unlockFieldsFromPreviousWorlds(state: IGameState) {
        const highestLevelCompleted = Game.player.getHighestLevelCompleted(state);
        const fieldsToUnlock: string[] = [];
        let worldNumber = 1;
        while (worldNumber < state.worldState.worldNumber) {
            const fields = WorldDataHelper.getAllFieldsData(worldNumber);
            fields.forEach(field => {
                if (
                    highestLevelCompleted >= field.requiredLevel[0] &&
                    state.playerState.fields.indexOf(`${field.id}_0`) === -1
                ) {
                    fieldsToUnlock.push(`${field.id}_0`);
                }
                if (
                    Game.player.getHighestLevelCompleted(state) >= field.requiredLevel[1] &&
                    state.playerState.fields.indexOf(`${field.id}_1`) === -1
                ) {
                    fieldsToUnlock.push(`${field.id}_1`);
                }
            });
            worldNumber++;
        }
        PlayerActions.unlockFields(...fieldsToUnlock);
    }
}
